/** @jsxImportSource @emotion/react */
import { usePrioritisationMap } from '@app/contexts/ModuleMapContexts/PrioritisationMapContext'
import { Snackbar, useSnackbars } from '@app/contexts/SnackbarContext'
import { SimplifiedGeoJSONFeature } from '@app/redux/prioritisationMap/prioritisationMapSlice'
import {
  flattenToMapLayerById,
  useLayerDataFetcher,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { useLoadingState } from '@app/hooks/useLoadingState'
import { useLockedAsync } from '@app/hooks/useLockedAsync'
import { clientNameAtom } from '@app/state/clientsAtom'
import { logger } from '@app/utils/logger'
import { AxiosResponse } from 'axios'
import { useAtomValue } from 'jotai'
import { MapboxGeoJSONFeature } from 'mapbox-gl'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  HotspotHistogramDatum,
  HotspotLegend,
  HotspotVulnerabilitiesData,
} from './PrioritisationContext.types'
import apiClient from '@app/config/axios.config'

interface PrioritisationContextProps {
  valueWeightings: { [key: string]: number }
  setValueWeightings: (value_set_weights: { [key: string]: number }) => void
  elementGroups: ValueSet[]
  initializePrioritisation: () => void
  assessment: Assessment | null
  scenarioGroup: MCAScenario | null
  prioritisationLayers: PrioritisationMapLayer[] | null
  setScenarioGroup: (scenarioGroup: MCAScenario) => void
  area: string | null
  areaType: string | null
  setArea: (areaFeature: string) => void
  setAreaType: (areaType: string) => void
  areaFeature: SimplifiedGeoJSONFeature | null
  legend: HotspotLegend | null
  legendIsLoading: boolean
  legendIsInitialized: boolean
  setAreaFeature: (areaFeature: MapboxGeoJSONFeature) => void
  allScenarioGroups: MCAScenario[] | null
  adaptationAreaPriorities: {
    area: string
    areaGroup: string
    priorityIndex: number
    color: string
  }[]
  exposurePercIsLoading: boolean
  exposurePercIsInitialized: boolean
  adaptationAreaPrioritiesIsLoading: boolean
  adaptationAreaPrioritiesIsInitialized: boolean
  exposurePercByElement: { [key: string]: ElementExposure[] }
}

export interface ElementExposure {
  element: string
  percentage: number
  total_metric: number
  exposure: number
  unit: string
}

export interface ValueSet {
  value_set_id: string
  elements: {
    type: string
    weight: number
    label: string
  }[]
}

export interface PrioritisationMapLayer {
  layer_id: string
  display_name: string
  layer_type: string
}

export interface MCAHazardScenario {
  hazard_uid: string
  scenario_name: string
  display_name: string
}

export interface MCAScenario {
  scenario_id: string
  description: string
  hazard_scenarios: MCAHazardScenario[]
  parameters: {
    [key: string]: string | number | boolean | null
  }
}

export interface Assessment {
  assessment_id: string
  description: string
  scenario_ids: string[]
  value_set_config: {
    value_set_weights: { [key: string]: number }
  }
  is_default: boolean
}

export const prioritisationColorLegend = {
  'Very high': '#7D0000',
  High: '#F80000',
  Moderate: '#FF7A7B',
  Low: '#FFBBBA',
  'Very low': '#FFDADA',
}

export const PrioritisationContext = createContext({} as PrioritisationContextProps)
export const PrioritisationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const layersData = useLayerDataFetcher()
  const [valueWeightings, setValueWeightings] = useState<
    PrioritisationContextProps['valueWeightings']
  >({})
  const [elementGroups, setElementGroups] = useState<PrioritisationContextProps['elementGroups']>(
    [],
  )
  const { selectedPrioLayer, setSelectedPrioLayer } = usePrioritisationMap()
  const [assessment, setAssessment] = useState<Assessment | null>(null)
  const [prioritisationLayers, setPrioritisationLayers] = useState<PrioritisationMapLayer[] | null>(
    null,
  )
  const [scenarioGroup, setScenarioGroup] = useState<MCAScenario | null>(null)
  const [allScenarioGroups, setAllScenarioGroups] = useState<MCAScenario[] | null>(null)
  const [vulnerabilitiesCache, setVulnerabilitiesCache] = useState<{
    [key: string]: HotspotVulnerabilitiesData
  }>({})
  const [histogramCache, setHistogramCache] = useState<{
    [key: string]: {
      vulnerabilities: HotspotVulnerabilitiesData
      histogram: { [key: string]: HotspotHistogramDatum[] }
    }
  }>({})
  const clientName = useAtomValue(clientNameAtom)
  // Area Feature for the map's sake
  // "Area" is what really controls the sidedrawer content
  const [area, setArea] = useState<string | null>(null)
  const [areaFeature, setAreaFeatureState] = useState<SimplifiedGeoJSONFeature | null>(null)
  const [initializedForClient, setInitializedForClient] = useState<string | null>(null)

  const isInitializingRef = useRef(false)

  const [
    exposurePercByElement,
    setExposurePercByElement,
    exposurePercIsLoading,
    setExposurePercIsLoading,
    exposurePercIsInitialized,
  ] = useLoadingState<PrioritisationContextProps['exposurePercByElement']>({})
  const [
    adaptationAreaPriorities,
    setAdaptationAreaPriorities,
    adaptationAreaPrioritiesIsLoading,
    setAdaptationAreaPrioritiesIsLoading,
    adaptationAreaPrioritiesIsInitialized,
  ] = useLoadingState<PrioritisationContextProps['adaptationAreaPriorities']>([])
  const [legend, setLegend, legendIsLoading, setLegendIsLoading, legendIsInitialized] =
    useLoadingState<HotspotLegend | null>(null)
  const { displaySnackbar, removeSnackbar } = useSnackbars()

  const [areaType, setAreaType] = useState('')

  const setAreaFeature = (areaFeature: MapboxGeoJSONFeature) => {
    setAreaFeatureState(areaFeature as SimplifiedGeoJSONFeature)
    setArea(areaFeature.properties?.name)
  }

  const initializePrioritisation = useCallback(() => {
    if (!layersData || !clientName) {
      return
    }

    if (initializedForClient === clientName) {
      return
    }

    if (isInitializingRef.current) {
      return
    }

    isInitializingRef.current = true

    const abortController = new AbortController()

    const fetchAssessments = async () => {
      try {
        const assessmentsRes = await apiClient.get<Assessment[]>(`/prioritisation/assessments`, {
          signal: abortController.signal,
        })
        if (assessmentsRes.status !== 200) {
          return undefined
        }

        const firstAssessment = assessmentsRes.data[0]
        const defaultAssessment = assessmentsRes.data.find((a) => a.is_default)

        setAssessment(firstAssessment)
        setValueWeightings(firstAssessment.value_set_config.value_set_weights)

        return { firstAssessment, defaultAssessment }
      } catch (error) {
        logger.error('Error fetching assessments:', error)
        return undefined
      }
    }

    const fetchValueSets = async (fetchedAssessment: Assessment) => {
      const assessmentValueSets = fetchedAssessment.value_set_config.value_set_weights

      try {
        const valueSetsRes = await apiClient.get<ValueSet[]>(
          `/prioritisation/value_sets?value_sets=${Object.keys(assessmentValueSets).join(',')}`,
          {
            signal: abortController.signal,
          },
        )
        if (valueSetsRes.status !== 200) {
          return
        }

        setElementGroups(valueSetsRes.data)
      } catch (error) {
        logger.error('Error fetching value sets:', error)
      }
    }

    const fetchScenarios = async (defaultAssessment?: Assessment) => {
      try {
        const flatLayerData = flattenToMapLayerById(layersData)
        const scenariosRes = await apiClient.get<MCAScenario[]>(`/prioritisation/scenarios`, {
          signal: abortController.signal,
        })
        if (scenariosRes.status !== 200) {
          return
        }

        const scenariosWithHazardNames = scenariosRes.data.map((s) => ({
          ...s,
          hazard_scenarios: s.hazard_scenarios.map((hs) => ({
            ...hs,
            scenario_name: hs.display_name,
            display_name:
              flatLayerData[hs.hazard_uid]?.hazard_group ??
              flatLayerData[hs.hazard_uid]?.hazard_name ??
              hs.display_name,
          })),
        }))

        setAllScenarioGroups(scenariosWithHazardNames)

        const foundScenario = scenariosWithHazardNames.find((s) =>
          defaultAssessment?.scenario_ids.some((id) => id === s.scenario_id),
        )
        setScenarioGroup(foundScenario ?? scenariosWithHazardNames[0])
      } catch (error) {
        logger.error('Error fetching scenarios:', error)
      }
    }

    const fetchPrioritisationLayers = async () => {
      try {
        const layersRes = await apiClient.get<PrioritisationMapLayer[]>(`/prioritisation/layers`, {
          signal: abortController.signal,
        })
        if (layersRes.status !== 200) {
          return
        }

        setPrioritisationLayers(layersRes.data)
        setAreaType(layersRes.data[0]?.layer_type)
        if (!selectedPrioLayer) {
          setSelectedPrioLayer(layersRes.data[0])
        }
      } catch (error) {
        logger.error('Error fetching layers:', error)
      }
    }

    Promise.all([fetchPrioritisationLayers(), fetchAssessments()])
      .then(([_, assessmentResult]) => {
        if (!assessmentResult) {
          return Promise.reject('No assessment data')
        }

        const { firstAssessment, defaultAssessment } = assessmentResult

        return Promise.all([fetchScenarios(defaultAssessment), fetchValueSets(firstAssessment)])
      })
      .then(() => {
        setInitializedForClient(clientName)
        isInitializingRef.current = false
      })
      .catch((error) => {
        logger.error('Initialization failed:', error)
        isInitializingRef.current = false
      })

    return () => {
      abortController.abort()
    }
  }, [clientName, initializedForClient, layersData, selectedPrioLayer, setSelectedPrioLayer])

  // Loading Vulnerabilities Data
  const processVulnerabilities = (vulnData: HotspotVulnerabilitiesData) => {
    let uniqueIndices = 0
    let previousValue: null | number = null

    const priorities = Object.entries(vulnData.regions).flatMap(([areaName, area]) => {
      if (areaName === 'All') return []
      // color for total score by legend buckets
      const areaCategory = Object.entries(vulnData.legend).find(
        ([_, range]) => area.totalAreaScore >= range[0] && area.totalAreaScore <= range[1],
      )?.[0]
      if (previousValue === null || previousValue !== area.totalAreaScore) {
        uniqueIndices++
        previousValue = area.totalAreaScore
      }
      return [
        {
          area: areaName,
          areaGroup: areaType,
          priorityIndex: uniqueIndices,
          color: prioritisationColorLegend[areaCategory as keyof HotspotLegend],
        },
      ]
    })

    setLegend(vulnData.legend)
    setLegendIsLoading(false)
    setAdaptationAreaPriorities(priorities)
    setAdaptationAreaPrioritiesIsLoading(false)
  }
  const updateVulnerabilities = useLockedAsync(async () => {
    if (!scenarioGroup || !clientName || !layersData) return
    setAdaptationAreaPrioritiesIsLoading(true)
    const abortController = new AbortController()
    const sortedValueWeightings = Object.fromEntries(
      Object.entries(valueWeightings).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
    )

    const cacheKey =
      JSON.stringify(sortedValueWeightings) + '|' + clientName + '|' + scenarioGroup.scenario_id

    if (vulnerabilitiesCache[cacheKey]) {
      processVulnerabilities(vulnerabilitiesCache[cacheKey])
      return
    }

    const loadingMessage: Snackbar = {
      message: 'Loading...',
      type: 'info',
    }

    const loadingTimeout = setTimeout(() => {
      displaySnackbar(loadingMessage)
    }, 1000)

    const vulnerabilitiesResponse = await apiClient.post<HotspotVulnerabilitiesData>(
      `/prioritisation/vulnerabilities/${scenarioGroup.scenario_id}?region_group=${areaType}`,
      {
        value_set_weights: valueWeightings,
      },
      { signal: abortController.signal },
    )

    if (vulnerabilitiesResponse.status !== 200) {
      clearTimeout(loadingTimeout)
      removeSnackbar(loadingMessage)
      return
    }
    setVulnerabilitiesCache((prev) => ({
      ...prev,
      [cacheKey]: vulnerabilitiesResponse.data,
    }))
    processVulnerabilities(vulnerabilitiesResponse.data)

    clearTimeout(loadingTimeout)
    removeSnackbar(loadingMessage)
  })

  // Loading Histogram Data
  const processHistogram = (histoData: { [key: string]: HotspotHistogramDatum[] }) => {
    const flatLayerData = flattenToMapLayerById(layersData)
    const exposurePercByElement: { [key: string]: ElementExposure[] } = Object.entries(
      histoData,
    ).reduce(
      (acc, datum) => {
        acc[datum[0]] = datum[1].map((h) => ({
          element: flatLayerData[h.element_id]?.display_name ?? h.element_id,
          percentage: Math.round(h.percentage_exposed * 10) / 10,
          total_metric: Math.round(h.total_metric * 10) / 10,
          exposure: Math.round(h.exposed_metric * 10) / 10,
          // Hide unit for count metrics
          unit:
            flatLayerData[h.element_id]?.metric_type !== 'count'
              ? (flatLayerData[h.element_id]?.unit ?? '')
              : '',
        }))
        return acc
      },
      {} as { [key: string]: ElementExposure[] },
    )
    setExposurePercByElement(exposurePercByElement)
    setExposurePercIsLoading(false)
  }
  const updateHistogram = useLockedAsync(async () => {
    if (!scenarioGroup || !clientName || !layersData) return
    setExposurePercIsLoading(true)
    const abortController = new AbortController()
    const sortedValueWeightings = Object.fromEntries(
      Object.entries(valueWeightings).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
    )
    // Generate a cache key out of the value weightings and the scenario group
    // This will be used to check if the histogram data is already cached
    const scenariosStrings = scenarioGroup.hazard_scenarios.reduce(
      (acc, hs) => {
        if (!acc[hs.display_name]) {
          acc[hs.display_name] = ''
        } else {
          acc[hs.display_name] += ','
        }
        acc[hs.display_name] += hs.hazard_uid
        return acc
      },
      {} as { [key: string]: string },
    )
    const cacheKey =
      JSON.stringify(sortedValueWeightings) +
      '|' +
      Object.values(scenariosStrings).join('-') +
      '|' +
      clientName +
      '|' +
      area
    if (histogramCache[cacheKey]) {
      processHistogram(histogramCache[cacheKey].histogram)
      return
    }

    // Not cached, so build data per hazard type manually
    const areaQuery = area ? `?regions=${area}` : ''
    const collatedHistogramData: { [key: string]: HotspotHistogramDatum[] } = {}
    const promises: Promise<AxiosResponse<HotspotHistogramDatum[]>>[] = [
      apiClient.post<HotspotHistogramDatum[]>(
        `/prioritisation/histogram_data/${scenarioGroup.hazard_scenarios
          .map((x) => x.hazard_uid)
          .join(',')}${areaQuery}`,
        { signal: abortController.signal },
      ),
    ]

    for (const key in scenariosStrings) {
      const scenariosString = scenariosStrings[key]

      promises.push(
        apiClient.post<HotspotHistogramDatum[]>(
          `/prioritisation/histogram_data/${scenariosString}${areaQuery}`,
          { signal: abortController.signal },
        ),
      )
    }

    await Promise.all(promises).then((responses) => {
      responses.forEach((response, index) => {
        if (response.status === 200) {
          if (index === 0) {
            collatedHistogramData['All hazards'] = response.data
          } else {
            const scenarioKey = Object.keys(scenariosStrings)[index - 1]
            collatedHistogramData[scenarioKey] = response.data
          }
        }
      })
    })

    setHistogramCache((prev) => ({
      ...prev,
      [cacheKey]: {
        vulnerabilities: vulnerabilitiesCache[cacheKey],
        histogram: collatedHistogramData,
      },
    }))
    processHistogram(collatedHistogramData)
  })

  useEffect(() => {
    if (
      scenarioGroup &&
      clientName &&
      initializedForClient === clientName &&
      layersData &&
      areaType
    ) {
      updateVulnerabilities()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scenarioGroup,
    allScenarioGroups,
    valueWeightings,
    assessment,
    areaType,
    clientName,
    layersData,
    initializedForClient,
  ])

  useEffect(() => {
    if (scenarioGroup && clientName && layersData) {
      updateHistogram()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioGroup, allScenarioGroups, valueWeightings, assessment, clientName, layersData, area])

  return (
    <PrioritisationContext.Provider
      value={{
        valueWeightings,
        setValueWeightings,
        elementGroups,
        initializePrioritisation,
        assessment,
        scenarioGroup,
        prioritisationLayers,
        area,
        areaType,
        setArea,
        setAreaType,
        legend,
        legendIsLoading,
        legendIsInitialized,
        areaFeature,
        setAreaFeature,
        allScenarioGroups,
        setScenarioGroup,
        adaptationAreaPriorities,
        exposurePercByElement,
        exposurePercIsLoading,
        adaptationAreaPrioritiesIsLoading,
        exposurePercIsInitialized,
        adaptationAreaPrioritiesIsInitialized,
      }}
    >
      {children}
    </PrioritisationContext.Provider>
  )
}

export const usePrioritisationContext = () => useContext(PrioritisationContext)
